import { useCallback, useEffect, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { readRfqInvitations } from "../../infrastructure/rfqs/rfq-invitation-gateway";
import type { Rfq, RfqInvitation } from "../../domain/rfq-types";
import type { ProcurementScope } from "../shared/useProcurementScope";
import { invitationErrorMessage } from "./rfq-presentation";
import { useRfqReadScope } from "./useRfqReadScope";

export function useRfqInvitationData({ scope, rfq, canManageInvitations }: { scope: ProcurementScope; rfq: Rfq | null; canManageInvitations: boolean }) {
  const readScope = useRfqReadScope(scope, rfq?.id);
  const [state, setState] = useState<{ readScope: typeof readScope; invitations: RfqInvitation[]; invitationLoading: boolean; invitationsLoaded: boolean; invitationError: string }>({ readScope, invitations: [], invitationLoading: false, invitationsLoaded: false, invitationError: "" });
  const sequence = useRef(0);
  const setInvitationError: Dispatch<SetStateAction<string>> = useCallback(value => {
    if (!readScope.isCurrent()) return;
    setState(current => ({ ...(current.readScope === readScope ? current : { invitations: [], invitationLoading: false, invitationsLoaded: false }), readScope, invitationError: typeof value === "function" ? value(current.readScope === readScope ? current.invitationError : "") : value }));
  }, [readScope]);
  const loadInvitations = useCallback(async (rfqId: string) => {
    if (!readScope.isCurrent() || rfqId !== rfq?.id || !canManageInvitations) return false;
    const ticket = ++sequence.current;
    const isCurrent = () => readScope.isCurrent() && ticket === sequence.current;
    setState(current => ({ readScope, invitations: current.readScope === readScope ? current.invitations : [], invitationLoading: true, invitationsLoaded: false, invitationError: "" }));
    try {
      const invitations = await readRfqInvitations(rfqId);
      if (!isCurrent()) return false;
      setState({ readScope, invitations, invitationLoading: false, invitationsLoaded: true, invitationError: "" });
      return true;
    } catch (value) {
      if (isCurrent()) setInvitationError(invitationErrorMessage(value));
      return false;
    } finally {
      if (isCurrent()) setState(current => ({ ...current, invitationLoading: false }));
    }
  }, [readScope, rfq?.id, canManageInvitations, setInvitationError]);
  useEffect(() => {
    if (rfq?.id && rfq.status === "PUBLISHED") void loadInvitations(rfq.id);
  }, [rfq?.id, rfq?.status, loadInvitations]);
  return { invitations: state.readScope === readScope && canManageInvitations ? state.invitations : [],
    invitationsLoaded: state.readScope === readScope && canManageInvitations && state.invitationsLoaded && !state.invitationLoading,
    invitationLoading: state.readScope === readScope && state.invitationLoading,
    invitationError: state.readScope === readScope ? state.invitationError : "",
    setInvitationError, loadInvitations };
}
